// EDEN · the Book Reader — the real page of a department book, opened over the screen the teacher is on.
//
// BOOK-READER-1 (owner ruling 2026-10-07): every page reference on the Books card that has a TRUSTED page map
// carries the book's id and its PDF pages (scripts/build-lesson-refs.mjs); its button opens this reader on
// those pages. The pictures come one at a time from /m/<book-id>/page/<n> (functions/m/[[path]].ts), which
// only answers a signed-in EDEN account. Nothing here is a PDF and nothing is stored offline.
//
// What a teacher gets: the page as large as the screen allows, the PRINTED page number in big type,
// ‹ › and swipe for the next page, + / − / double-tap / pinch to zoom, a strip of this lesson's pages,
// "Show the whole unit", and "Back to the lesson" (the phone's Back button does the same).
//
// Built to grow (Job 3, BOOK-READER-2) without a rewrite: the bar has a tools slot (`[data-rd-tools]`) for
// "Teacher's Guide" and "Present", the stage has a side pane (`[data-rd-side]`) for the guide's steps as text,
// and every page carries its book id + PDF page, which is the key the teacher-pages and audio joins will use.

import "./styles/reader.css";
import { esc } from "./dom";
import { icon } from "./icons";
import { jpegsToPdf } from "./jpeg-pdf";

/** A run of PDF pages whose printed number is pdf + delta: [firstPdf, lastPdf, delta]. */
export type PageSeg = [first: number, last: number, delta: number];
export interface GuideTarget { book_id: string; book: string; label: string; pdf: PageSeg[] }
/** A class-audio track the gate serves ("2.01"), with the words a teacher sees ("Test 2 · Part 1"). */
export interface Track { id: string; label: string }
const LG_TEACHING = /^(?:LG[1-6]|LGB[12])-SB$/;
interface TeachingPage {
  printed: number | null; title: string; guide?: GuideTarget;
  steps: string[]; tracks: { id: string; available: boolean }[]; note?: string;
}
/** Extracted guide paragraphs are private content. Fetch one page through the signed-in gate,
 *  never bundle the full teacher text into a public browser asset or offline cache. */
async function teachingPage(t: ReaderTarget, page: Page): Promise<TeachingPage | null> {
  const tail = t.missing ? `missing/${t.missing}` : String(page.pdf);
  const response = await fetch(`/m/${encodeURIComponent(t.bookId)}/guide/${tail}`, { credentials: "same-origin", cache: "no-store" });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(response.status === 401 ? "session" : "guide");
  return await response.json() as TeachingPage;
}

export interface ReaderTarget {
  /** the teacher-facing book name ("Oxford Grammar for Schools 5") */
  book: string;
  /** the shelf id the /m/ gate serves ("OGFS5-SB") */
  bookId: string;
  /** what these pages are ("Unit 7: The future") */
  label: string;
  /** this lesson's pages */
  pages: PageSeg[];
  /** the whole unit, when it is more than this lesson's pages */
  unit?: { label: string; pages: PageSeg[] };
  guide?: GuideTarget;
  /** A printed Student Book page absent from this supplied PDF; its guide still exists. */
  missing?: number;
  /** Companion notes and answers stay out of the classroom Present screen. */
  teacherOnly?: boolean;
  /** Labelled audio for books without guide notes: readers, exam papers, practice worksheets (T1 review). */
  tracks?: Track[];
  /** Signed-in downloads, e.g. a practice worksheet's original Word file. */
  downloads?: { label: string; href: string }[];
  /** Opened from the Books tab: "Back to Books", and `unit` is the whole book rather than the unit. */
  from?: "library";
}

interface Page { pdf: number; printed: number }

/** Every page of a segment list, in order. */
export function expandPages(segs: PageSeg[]): Page[] {
  const out: Page[] = [];
  for (const [a, b, d] of segs) for (let p = a; p <= b && out.length < 400; p++) out.push({ pdf: p, printed: p + d });
  return out;
}

/** "41–45", "46", or null when the printed numbers do not run on (a test row: 16, 36, 56). */
export function printedRange(segs: PageSeg[]): string | null {
  const pages = expandPages(segs).map((p) => p.printed);
  if (!pages.length) return null;
  const runsOn = pages.every((p, i) => i === 0 || p === pages[i - 1]! + 1);
  if (!runsOn) return null;
  return pages.length === 1 ? `${pages[0]}` : `${pages[0]}–${pages[pages.length - 1]}`;
}

export function pageUrl(bookId: string, pdf: number, hd = false): string {
  return `/m/${encodeURIComponent(bookId)}/page/${pdf}${hd ? "?hd=1" : ""}`;
}

/** Teacher's Guide / Teacher's Book pages are small two-column print: they always ask for the sharp copy (teacher
 *  review, 15 Oct 2026 — "the Teacher's Guide is very poor quality; they can't read it"). The gate falls back to the
 *  standard picture where no sharp copy is online yet, so asking is always safe. Any book asks once zoomed in. */
const GUIDE_BOOK = /-(?:TB|TG)$/;
/** A guide filed under an older shelf id (Blueprint 5/6 "-SB", Blueprint 4, Grammar for Schools 1–2) is still a guide. */
const GUIDE_NAME = /teacher['’]s (?:guide|book)/i;
function wantsHd(t: ReaderTarget, zoom: number): boolean {
  return zoom > 1 || !!t.teacherOnly || GUIDE_BOOK.test(t.bookId) || GUIDE_NAME.test(t.book);
}

/** How many pages one download may carry: a lesson's pages or a unit, never a whole book in one go. */
export const DOWNLOAD_MAX_PAGES = 40;

// ── the open buttons (rendered as HTML strings by the Books card; one listener handles them all) ──
const targets = new Map<string, ReaderTarget>();
const byKey = new Map<string, string>();

/** The button that opens `t`. Plain words: "Open pages 41–45" / "Open page 46" / "Open these 3 pages". */
export function openButtonHtml(t: ReaderTarget, cls = ""): string {
  const id = registerTarget(t);
  const range = printedRange(t.pages);
  const n = expandPages(t.pages).length;
  const words = t.missing ? `Guide for page ${t.missing} · not in this PDF` : range ? (range.includes("–") ? `Open pages ${range}` : `Open page ${range}`) : `Open these ${n} pages`;
  return `<button type="button" class="bk-open${cls ? " " + cls : ""}" data-book-open="${id}">${icon("book", "ico-sm")}<span>${esc(words)}</span></button>`;
}

/** The `data-book-open` id for `t` (one id per distinct target), for screens that draw their own button. */
export function registerTarget(t: ReaderTarget): string {
  const key = JSON.stringify([t.bookId, t.label, t.pages, t.unit ?? null, t.guide ?? null, t.missing ?? null, t.teacherOnly ?? false,
    t.tracks ?? null, t.downloads ?? null, t.from ?? null]);
  let id = byKey.get(key);
  if (!id) {
    id = `rd${byKey.size + 1}`;
    byKey.set(key, id);
    targets.set(id, t);
  }
  return id;
}

// ── the reader ─────────────────────────────────────────────────────────────────────────────────
const ZOOMS = [1, 1.5, 2, 3];
const st = {
  t: null as ReaderTarget | null,
  set: [] as Page[],
  lesson: new Set<number>(),
  i: 0,
  whole: false,
  zoom: 1,
  opener: null as HTMLElement | null,
  pushed: false,
  closingByButton: false,
  notes: true,
  present: false,
  guide: null as GuideTarget | null,
  teaching: null as TeachingPage | null,
  /** The Student Book page's notes, kept on screen while its Teacher's Guide page is open (teacher review, 15 Oct:
   *  "if they click Teacher's Guide everything disappears"). */
  carried: null as { entry: TeachingPage; bookId: string } | null,
};
const returns: { t: ReaderTarget; i: number; whole: boolean }[] = [];
let generation = 0;
let playing: string | null = null;

let root: HTMLElement | null = null;
const q = <T extends HTMLElement>(sel: string): T => root!.querySelector<T>(sel)!;

function build(): HTMLElement {
  const el = document.createElement("div");
  el.id = "eden-reader";
  el.className = "reader";
  el.hidden = true;
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "rd-title");
  el.innerHTML = `
    <header class="rd-bar">
      <button type="button" class="rd-back" data-rd="close" aria-label="Back to the lesson">${icon("back")}<span class="rd-lg">Back to the lesson</span><span class="rd-sm">Back</span></button>
      <div class="rd-title" id="rd-title"><b class="rd-book"></b><span class="rd-label"></span></div>
      <div class="rd-tools" data-rd-tools>
        <button type="button" class="rd-tool rd-word" data-rd="guide" hidden>Teacher’s Guide page</button>
        <button type="button" class="rd-tool rd-word" data-rd="notes" aria-pressed="true" hidden title="Show or hide the Teacher’s Guide notes">Notes</button>
        <button type="button" class="rd-tool rd-word" data-rd="download" aria-haspopup="true" aria-expanded="false" title="Save these pages">Download</button>
        <button type="button" class="rd-tool rd-word" data-rd="present" aria-pressed="false">Present</button>
        <button type="button" class="rd-tool" data-rd="zoom-out" aria-label="Make the page smaller">−</button>
        <button type="button" class="rd-tool" data-rd="zoom-in" aria-label="Make the page bigger">+</button>
      </div>
    </header>
    <div class="rd-pageno"><span class="rd-pg" aria-live="polite"></span><span class="rd-of"></span><span class="rd-dl" hidden></span></div>
    <div class="rd-dlmenu" data-rd-dlmenu hidden role="menu" aria-label="Download">
      <button type="button" role="menuitem" data-rd="dl-page">This page (picture)</button>
      <button type="button" role="menuitem" data-rd="dl-pdf">All these pages (one PDF)</button>
      <p class="rd-dlmsg" role="status" hidden></p>
    </div>
    <div class="rd-audio" hidden><div class="rd-tracks" aria-label="Class audio tracks"></div>
      <div class="rd-player" hidden><span class="rd-track-name"></span><audio controls preload="none"></audio></div>
      <p class="rd-audio-msg" role="status" hidden></p>
    </div>
    <div class="rd-main">
      <div class="rd-view" data-rd-view tabindex="-1">
        <img class="rd-img" alt="" draggable="false" />
        <div class="rd-msg" hidden></div>
      </div>
      <aside class="rd-side" data-rd-side hidden></aside>
    </div>
    <footer class="rd-strip">
      <div class="rd-nav">
        <button type="button" class="rd-arrow" data-rd="prev" aria-label="Previous page">${icon("back")}</button>
        <div class="rd-thumbs" data-rd-thumbs></div>
        <button type="button" class="rd-arrow" data-rd="next" aria-label="Next page">${icon("chevron")}</button>
      </div>
      <button type="button" class="rd-unit" data-rd="unit" hidden></button>
    </footer>`;
  document.body.appendChild(el);
  root = el;
  wire(el);
  return el;
}

export function openReader(t: ReaderTarget, opener: HTMLElement | null = null, nested = false): void {
  root ??= build();
  if (!nested) { returns.length = 0; st.carried = null; }
  stopAudio();
  st.t = t;
  if (!nested || t.teacherOnly) exitPresent();
  st.opener = opener;
  st.whole = false;
  st.zoom = 1;
  const lesson = t.missing ? [{ pdf: 0, printed: t.missing }] : expandPages(t.pages);
  st.lesson = new Set(lesson.map((p) => p.pdf));
  st.set = lesson;
  st.i = 0;
  if (!st.set.length) return;
  root.hidden = false;
  document.body.classList.add("reader-open");
  for (const id of ["app", "eden-nav", "eden-tabbar"]) document.getElementById(id)?.setAttribute("inert", "");
  // the phone's Back button closes the reader instead of leaving the lesson
  if (!st.pushed) {
    history.pushState({ ...(history.state ?? {}), edenReader: true }, "", location.href);
    st.pushed = true;
  }
  render();
  q<HTMLButtonElement>(".rd-back").focus();
}

function closeReader(fromHistory = false): void {
  if (!root || root.hidden) return;
  if (!fromHistory && returns.length) {
    const back = returns.pop()!;
    st.carried = null;
    const opener = st.opener;
    openReader(back.t, opener, true);
    st.whole = back.whole;
    st.set = expandPages(back.whole && back.t.unit ? back.t.unit.pages : back.t.pages);
    if (back.t.missing) st.set = [{ pdf: 0, printed: back.t.missing }];
    st.i = back.i;
    render();
    return;
  }
  generation++;
  returns.length = 0;
  stopAudio();
  exitPresent();
  root.hidden = true;
  document.body.classList.remove("reader-open");
  for (const id of ["app", "eden-nav", "eden-tabbar"]) document.getElementById(id)?.removeAttribute("inert");
  q<HTMLImageElement>(".rd-img").removeAttribute("src");
  if (st.pushed && !fromHistory) {
    st.closingByButton = true;
    history.back();
  }
  st.pushed = false;
  st.opener?.focus();
}

function go(i: number): void {
  if (i < 0 || i >= st.set.length || i === st.i) return;
  st.i = i;
  // Let's Go guide tracks belong to one page; a reader's or a test's tracks run across the pages.
  if (!st.t?.tracks?.length) stopAudio();
  st.zoom = 1;
  render();
}

function render(): void {
  const t = st.t;
  if (!t || !root) return;
  const page = st.set[st.i]!;
  const current = ++generation;
  // From the Books tab, "Show the whole book" pages through OTHER parts too: this part's answers, audio and Word
  // files belong to its own pages only.
  const own = !(t.from === "library" && st.whole && !st.lesson.has(page.pdf));
  st.guide = own ? t.guide ?? null : null;
  st.teaching = null;
  const from = returns[returns.length - 1]?.t;
  const back = from ? (LG_TEACHING.test(from.bookId) ? "Back to student page" : "Back to the pages") : t.from === "library" ? "Back to Books" : "Back to the lesson";
  q<HTMLElement>(".rd-back span.rd-lg").textContent = back;
  q<HTMLButtonElement>(".rd-back").setAttribute("aria-label", back);
  q<HTMLButtonElement>('[data-rd="guide"]').hidden = !st.guide;
  q<HTMLButtonElement>('[data-rd="guide"]').textContent = t.guide ? "Answers" : "Teacher’s Guide page";
  closeDownloadMenu();
  q<HTMLButtonElement>('[data-rd="notes"]').hidden = true;
  q<HTMLButtonElement>('[data-rd="notes"]').setAttribute("aria-pressed", String(st.notes));
  q<HTMLButtonElement>('[data-rd="present"]').textContent = st.present ? "Exit present" : "Present";
  q<HTMLButtonElement>('[data-rd="present"]').setAttribute("aria-pressed", String(st.present));
  q<HTMLButtonElement>('[data-rd="present"]').hidden = !!t.teacherOnly || !!t.missing;
  q<HTMLElement>('[data-rd-side]').hidden = true;
  q<HTMLElement>('[data-rd-side]').replaceChildren();
  // On a Teacher's Guide page opened from a Student Book page, that page's notes stay beside it.
  if (t.teacherOnly && st.carried) renderCarried(st.carried);
  q<HTMLElement>(".rd-audio").hidden = true;
  q<HTMLElement>(".rd-tracks").replaceChildren();
  if (own && t.tracks?.length && !t.missing) renderLabelledTracks(t.tracks);
  else if (playing && !LG_TEACHING.test(t.bookId)) stopAudio();
  const dl = q<HTMLElement>(".rd-dl");
  dl.hidden = !own || !t.downloads?.length;
  dl.innerHTML = (t.downloads ?? []).map((d) => `<a class="rd-dl-link" href="${esc(d.href)}" download>${icon("download", "ico-sm")}<span>${esc(d.label)}</span></a>`).join("");
  const label = st.whole && t.unit ? t.unit.label : t.label;
  q(".rd-book").textContent = t.book;
  q(".rd-label").textContent = label;
  q(".rd-pg").textContent = `Page ${page.printed}`;
  const range = printedRange(st.whole && t.unit ? t.unit.pages : t.pages);
  q(".rd-of").textContent = st.set.length > 1
    ? `${st.i + 1} of ${st.set.length}${st.whole ? (t.from === "library" ? " in the book" : " in the unit") : range ? (t.from === "library" ? ` · pages ${range}` : ` · this lesson: pages ${range}`) : t.from === "library" ? "" : " for this lesson"}`
    : "";

  const img = q<HTMLImageElement>(".rd-img");
  const msg = q<HTMLElement>(".rd-msg");
  msg.hidden = true;
  img.hidden = false;
  img.classList.add("loading");
  img.alt = `${t.book}, page ${page.printed}`;
  if (t.missing) {
    img.hidden = true;
    img.removeAttribute("src");
    msg.hidden = false;
    msg.innerHTML = `<p>Student Book page ${page.printed} is not in this PDF.</p><p>Use the printed book. Its guide notes and tracks are available here.</p>`;
  } else img.src = pageUrl(t.bookId, page.pdf, wantsHd(t, st.zoom));
  applyZoom();
  void renderTeaching(t, page, current);

  // neighbours load in the background, so the next page is ready
  for (const j of [st.i + 1, st.i - 1]) {
    const n = st.set[j];
    if (n && n.pdf > 0) new Image().src = pageUrl(t.bookId, n.pdf, wantsHd(t, 1));
  }

  q<HTMLButtonElement>('[data-rd="prev"]').disabled = st.i === 0;
  q<HTMLButtonElement>('[data-rd="next"]').disabled = st.i === st.set.length - 1;
  q<HTMLButtonElement>('[data-rd="zoom-out"]').disabled = st.zoom <= ZOOMS[0]!;
  q<HTMLButtonElement>('[data-rd="zoom-in"]').disabled = st.zoom >= ZOOMS[ZOOMS.length - 1]!;

  // the strip is rebuilt only when its set of pages changes; a page turn just moves the marker
  const thumbs = q<HTMLElement>("[data-rd-thumbs]");
  const setKey = `${t.bookId}|${st.set.map((p) => p.pdf).join(",")}|${st.whole}|${t.missing ?? ""}`;
  if (thumbs.dataset.set !== setKey) {
    thumbs.dataset.set = setKey;
    thumbs.innerHTML = st.set.map((p, j) => `<button type="button" class="rd-thumb${st.whole && st.lesson.has(p.pdf) ? " in-lesson" : ""}" data-rd-go="${j}"
      aria-label="Page ${p.printed}${st.whole && st.lesson.has(p.pdf) ? " (this lesson)" : ""}">${p.pdf > 0 ? `<img alt="" loading="lazy" src="${esc(pageUrl(t.bookId, p.pdf))}" />` : ""}<span>${p.printed}</span></button>`).join("");
  }
  thumbs.querySelectorAll<HTMLElement>("[data-rd-go]").forEach((b) => {
    if (Number(b.dataset.rdGo) === st.i) b.setAttribute("aria-current", "page");
    else b.removeAttribute("aria-current");
  });
  thumbs.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "center" });

  const unitBtn = q<HTMLButtonElement>('[data-rd="unit"]');
  unitBtn.hidden = !t.unit;
  unitBtn.textContent = t.from === "library"
    ? (st.whole ? "Show only these pages" : "Show the whole book")
    : (st.whole ? "Show only this lesson's pages" : "Show the whole unit");
}

function audioElement(): HTMLAudioElement { return q<HTMLAudioElement>(".rd-player audio"); }
function stopAudio(): void {
  if (!root) return;
  const audio = audioElement();
  audio.pause(); audio.removeAttribute("src"); audio.load(); playing = null;
  q<HTMLElement>(".rd-player").hidden = true;
}
function exitPresent(): void {
  st.present = false;
  root?.classList.remove("rd-present");
  if (document.fullscreenElement === root) void document.exitFullscreen().catch(() => {});
}
function renderTracks(entry: TeachingPage, bookId: string | null = null): void {
  const audio = q<HTMLElement>(".rd-audio");
  audio.hidden = !entry.tracks.length;
  q<HTMLElement>(".rd-audio-msg").hidden = true;
  const book = bookId ? ` data-rd-book="${esc(bookId)}"` : "";
  q<HTMLElement>(".rd-tracks").innerHTML = entry.tracks.map((track) => track.available
    ? `<button class="rd-track" type="button" data-rd-track="${esc(track.id)}"${book} aria-label="Play track ${esc(track.id)}">▶ ${esc(track.id)}</button>`
    : `<span class="rd-track-unavailable">Track ${esc(track.id)} · audio unavailable</span>`).join("");
}
/** The notes side pane: what it is, in one line, then the Teacher's Guide steps as easy-to-read text. */
function notesHtml(entry: TeachingPage, carried = false): string {
  const why = carried
    ? `Notes for Student Book page ${entry.printed ?? ""} — the same steps as this Teacher’s Guide page, typed out so they are easy to read.`
    : "Teacher’s Guide notes for this page — typed out so they are easy to read. “Teacher’s Guide page” shows the original.";
  return `<p class="rd-side-why">${esc(why)}</p><h2>${esc(entry.title || "Teacher’s Guide notes")}</h2>${entry.note ? `<p class="rd-guide-note">${esc(entry.note)}</p>` : ""}
      ${entry.steps.map((step) => `<p>${esc(step)}</p>`).join("")}`;
}
function renderCarried(c: { entry: TeachingPage; bookId: string }): void {
  const side = q<HTMLElement>('[data-rd-side]');
  if (!c.entry.steps.length && !c.entry.note) return;
  side.innerHTML = notesHtml(c.entry, true);
  side.hidden = !st.notes || st.present;
  q<HTMLButtonElement>('[data-rd="notes"]').hidden = false;
  renderTracks(c.entry, c.bookId);
}
/** Up to six tracks as buttons with their words; more (a reader's whole CD) as one list and a Play button. */
function renderLabelledTracks(tracks: Track[]): void {
  q<HTMLElement>(".rd-audio").hidden = false;
  q<HTMLElement>(".rd-audio-msg").hidden = true;
  q<HTMLElement>(".rd-tracks").innerHTML = tracks.length <= 6
    ? tracks.map((track) => `<button class="rd-track rd-track-l" type="button" data-rd-track="${esc(track.id)}" data-rd-name="${esc(track.label)}" aria-label="Play ${esc(track.label)}">▶ ${esc(track.label)}</button>`).join("")
    : `<label class="rd-pick"><span class="sr-only">Track</span><select data-rd-pick><option value="" disabled selected>Pick the track number on the page</option>${
        tracks.map((track) => `<option value="${esc(track.id)}">${esc(track.label)}</option>`).join("")}</select></label>
       <button class="rd-track" type="button" data-rd="play-pick">▶ Play</button>`;
  markPlaying();
}
/** The track buttons (and the list) show what is playing — after a play/pause, and after a page turn redraws them. */
function markPlaying(): void {
  if (!root) return;
  const paused = audioElement().paused;
  root.querySelectorAll<HTMLButtonElement>("[data-rd-track]").forEach((button) => {
    const active = button.dataset.rdTrack === playing && !paused;
    const name = button.dataset.rdName;
    button.textContent = `${active ? "❚❚" : "▶"} ${name ?? button.dataset.rdTrack}`;
    button.setAttribute("aria-label", `${active ? "Pause" : "Play"} ${name ?? `track ${button.dataset.rdTrack}`}`);
  });
  const pick = root.querySelector("[data-rd-pick]") as HTMLSelectElement | null;
  if (pick && playing && [...pick.options].some((o) => o.value === playing)) pick.value = playing;
}
async function renderTeaching(t: ReaderTarget, page: Page, current: number): Promise<void> {
  if (!LG_TEACHING.test(t.bookId) || t.teacherOnly) return;
  try {
    const entry = await teachingPage(t, page);
    if (generation !== current || root?.hidden) return;
    if (!entry) return;
    st.teaching = entry;
    st.guide = entry.guide ?? t.guide ?? null;
    q<HTMLButtonElement>('[data-rd="guide"]').hidden = !st.guide;
    q<HTMLButtonElement>('[data-rd="notes"]').hidden = !entry.steps.length && !entry.note;
    const side = q<HTMLElement>('[data-rd-side]');
    side.hidden = !st.notes || st.present || (!entry.steps.length && !entry.note);
    side.innerHTML = notesHtml(entry);
    renderTracks(entry);
    applyZoom();
  } catch (error: unknown) {
    if (generation !== current || root?.hidden) return;
    const side = q<HTMLElement>('[data-rd-side]');
    side.hidden = st.present;
    side.innerHTML = error instanceof Error && error.message === "session"
      ? `<p>Your sign-in has ended, so the guide notes are hidden.</p><a class="btn secondary" href="/signin">Sign in again</a>`
      : `<p>The guide notes did not load.</p><button type="button" class="btn secondary" data-rd="retry">Try again</button>`;
    if (error instanceof Error && error.message === "session") stopAudio();
  }
}
async function playTrack(id: string, bookOverride?: string): Promise<void> {
  const t = st.t;
  const named = t?.tracks?.find((track) => track.id === id);
  const fromCarried = !!bookOverride && !!st.carried?.entry.tracks.some((track) => track.id === id && track.available);
  if (!t || (!named && !fromCarried && !st.teaching?.tracks.some((track) => track.id === id && track.available))) return;
  const book = fromCarried ? bookOverride! : t.bookId;
  const audio = audioElement();
  if (playing === id && !audio.paused) { audio.pause(); return; }
  if (playing !== id || audio.error) { audio.pause(); audio.src = `/m/${encodeURIComponent(book)}/audio/${encodeURIComponent(id)}`; audio.load(); playing = id; }
  q<HTMLElement>(".rd-track-name").textContent = named?.label ?? `Track ${id}`;
  q<HTMLElement>(".rd-player").hidden = false;
  q<HTMLElement>(".rd-audio-msg").hidden = true;
  try { await audio.play(); }
  catch { if (playing === id) void audioFailure(); }
}
async function audioFailure(): Promise<void> {
  const id = playing, t = st.t;
  if (!id || !t) return;
  let status = 0;
  try { status = (await fetch(`/m/${encodeURIComponent(t.bookId)}/audio/${encodeURIComponent(id)}`, { method: "HEAD", cache: "no-store" })).status; } catch { /* offline */ }
  if (playing !== id || st.t !== t) return;
  const msg = q<HTMLElement>(".rd-audio-msg");
  msg.hidden = false;
  msg.textContent = status === 401 ? "Your sign-in has ended. Sign in again to play audio." : status === 404 ? `Track ${id} is not online yet.` : "The track did not play. Check the connection and tap the track to try again.";
}

/** Zoom 1 = the whole page fits the screen; above 1 the picture grows and the view scrolls (pan with a finger). */
function applyZoom(keepCentre = false): void {
  const view = q<HTMLElement>("[data-rd-view]");
  const img = q<HTMLImageElement>(".rd-img");
  // Zoomed in: ask for the sharp copy of this page (the gate answers with the standard one where none is online).
  const at = st.set[st.i];
  if (st.t && at && at.pdf > 0 && st.zoom > 1 && !/[?&]hd=1/.test(img.getAttribute("src") ?? "")) img.src = pageUrl(st.t.bookId, at.pdf, true);
  const cx = view.scrollLeft + view.clientWidth / 2, cy = view.scrollTop + view.clientHeight / 2;
  const oldW = img.offsetWidth || 1, oldH = img.offsetHeight || 1;
  view.classList.toggle("zoomed", st.zoom > 1);
  if (st.zoom <= 1 || !img.naturalWidth) {
    img.style.width = "";
    img.style.height = "";
  } else {
    const fit = Math.min(view.clientWidth / img.naturalWidth, view.clientHeight / img.naturalHeight);
    img.style.width = `${Math.round(img.naturalWidth * fit * st.zoom)}px`;
    img.style.height = `${Math.round(img.naturalHeight * fit * st.zoom)}px`;
  }
  if (keepCentre && st.zoom > 1) {
    view.scrollLeft = (cx / oldW) * img.offsetWidth - view.clientWidth / 2;
    view.scrollTop = (cy / oldH) * img.offsetHeight - view.clientHeight / 2;
  }
  q<HTMLButtonElement>('[data-rd="zoom-out"]').disabled = st.zoom <= ZOOMS[0]!;
  q<HTMLButtonElement>('[data-rd="zoom-in"]').disabled = st.zoom >= ZOOMS[ZOOMS.length - 1]!;
}

function zoomStep(dir: 1 | -1): void {
  const i = ZOOMS.findIndex((z) => z >= st.zoom - 0.01);
  const next = ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, (i < 0 ? 0 : i) + dir))]!;
  if (next === st.zoom) return;
  st.zoom = next;
  applyZoom(true);
}

/** A picture that will not load: ask the gate why, and say it in plain words. */
async function explainFailure(): Promise<void> {
  const t = st.t;
  const page = st.set[st.i];
  if (!t || !page || !root) return;
  let status = 0;
  try {
    status = (await fetch(pageUrl(t.bookId, page.pdf), { method: "HEAD", credentials: "same-origin", cache: "no-store" })).status;
  } catch { /* offline: status 0 */ }
  if (st.set[st.i] !== page) return; // the teacher has moved on
  const msg = q<HTMLElement>(".rd-msg");
  q<HTMLImageElement>(".rd-img").hidden = true;
  msg.hidden = false;
  msg.innerHTML = status === 401
    ? `<p>Your sign-in has ended, so the book pages are hidden.</p><a class="btn secondary" href="/signin">Sign in again</a>`
    : status === 404
      ? `<p>Page ${page.printed} of this book is not online yet.</p>`
      : `<p>The page did not load. Check the internet connection, then try again.</p><button type="button" class="btn secondary" data-rd="retry">Try again</button>`;
}

// ── Download (teacher review, 15 Oct 2026: "they can't download these pages even though we have the licence") ──
function closeDownloadMenu(): void {
  if (!root) return;
  const menu = root.querySelector<HTMLElement>("[data-rd-dlmenu]");
  if (menu) menu.hidden = true;
  root.querySelector<HTMLElement>('[data-rd="download"]')?.setAttribute("aria-expanded", "false");
}
function toggleDownloadMenu(): void {
  const menu = q<HTMLElement>("[data-rd-dlmenu]");
  const open = menu.hidden;
  menu.hidden = !open;
  q<HTMLElement>('[data-rd="download"]').setAttribute("aria-expanded", String(open));
  const n = Math.min(st.set.filter((p) => p.pdf > 0).length, DOWNLOAD_MAX_PAGES);
  const pdfBtn = q<HTMLButtonElement>('[data-rd="dl-pdf"]');
  pdfBtn.textContent = n > 1 ? `All ${n} pages (one PDF)` : "This page (PDF)";
  q<HTMLElement>(".rd-dlmsg").hidden = true;
  if (open) menu.querySelector<HTMLButtonElement>("button")?.focus();
}
function dlMessage(text: string): void {
  const m = q<HTMLElement>(".rd-dlmsg");
  m.hidden = !text;
  m.textContent = text;
}
function safeName(s: string): string {
  return s.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "EDEN pages";
}
function saveBlob(blob: Blob, name: string): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 4000);
}
async function fetchPicture(bookId: string, pdf: number, printed?: number | null): Promise<Uint8Array> {
  let r: Response;
  try { r = await fetch(pageUrl(bookId, pdf, true), { credentials: "same-origin" }); }
  catch { throw new Error("A page did not load. Check the internet connection and try again."); }
  if (r.status === 401) throw new Error("Your sign-in has ended. Sign in again, then download.");
  // a page picture that is not online: say so plainly — the connection is fine
  if (r.status === 404) throw new Error(`${printed ? `Page ${printed}` : "One of these pages"} is not online yet, so nothing was downloaded.`);
  if (!r.ok) throw new Error("A page did not load. Try again in a minute.");
  return new Uint8Array(await r.arrayBuffer());
}
async function downloadPage(): Promise<void> {
  const t = st.t, page = st.set[st.i];
  if (!t || !page || page.pdf <= 0) return dlMessage("This page is not in the online book.");
  dlMessage("Preparing the picture…");
  try {
    const bytes = await fetchPicture(t.bookId, page.pdf, page.printed);
    saveBlob(new Blob([bytes as BlobPart], { type: "image/jpeg" }), `${safeName(t.book)} - page ${page.printed}.jpg`);
    dlMessage("Saved. Look in your Downloads folder.");
  } catch (e) { dlMessage((e as Error).message); }
}
async function downloadPdf(): Promise<void> {
  const t = st.t;
  if (!t) return;
  const pages = st.set.filter((p) => p.pdf > 0).slice(0, DOWNLOAD_MAX_PAGES);
  if (!pages.length) return dlMessage("These pages are not in the online book.");
  const pics: Uint8Array[] = [];
  try {
    for (const [k, p] of pages.entries()) {
      dlMessage(`Preparing page ${k + 1} of ${pages.length}…`);
      pics.push(await fetchPicture(t.bookId, p.pdf, p.printed));
    }
    const first = pages[0]!.printed, last = pages[pages.length - 1]!.printed;
    const pdf = jpegsToPdf(pics, `${t.book} ${first === last ? `page ${first}` : `pages ${first}-${last}`}`);
    saveBlob(new Blob([pdf as BlobPart], { type: "application/pdf" }), `${safeName(t.book)} - ${first === last ? `page ${first}` : `pages ${first}-${last}`}.pdf`);
    dlMessage(st.set.length > DOWNLOAD_MAX_PAGES ? `Saved the first ${DOWNLOAD_MAX_PAGES} pages. Look in your Downloads folder.` : "Saved. Look in your Downloads folder.");
  } catch (e) { dlMessage((e as Error).message); }
}

function wire(el: HTMLElement): void {
  const img = el.querySelector<HTMLImageElement>(".rd-img")!;
  img.addEventListener("load", () => { img.classList.remove("loading"); applyZoom(); });
  img.addEventListener("error", () => { if (img.getAttribute("src")) void explainFailure(); });
  audioElement().addEventListener("error", () => { if (playing) void audioFailure(); });
  for (const event of ["play", "pause", "ended"]) audioElement().addEventListener(event, markPlaying);
  el.addEventListener("change", (e) => {
    const pick = (e.target as HTMLElement).closest("[data-rd-pick]") as HTMLSelectElement | null;
    if (pick?.value) void playTrack(pick.value);
  });

  el.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-rd], [data-rd-go], [data-rd-track]");
    if (!b) return;
    if (b.dataset.rdTrack) return void playTrack(b.dataset.rdTrack, b.dataset.rdBook);
    if (b.dataset.rdGo) return go(Number(b.dataset.rdGo));
    switch (b.dataset.rd) {
      case "close": return closeReader();
      case "prev": return go(st.i - 1);
      case "next": return go(st.i + 1);
      case "zoom-in": return zoomStep(1);
      case "zoom-out": return zoomStep(-1);
      case "retry": return render();
      case "play-pick": {
        const pick = root?.querySelector("[data-rd-pick]") as HTMLSelectElement | null;
        if (!pick) return;
        if (pick.value) return void playTrack(pick.value);
        const msg = q<HTMLElement>(".rd-audio-msg");
        msg.hidden = false;
        msg.textContent = "Pick the track number printed next to the activity, then press Play.";
        return pick.focus();
      }
      case "download": return toggleDownloadMenu();
      case "dl-page": return void downloadPage();
      case "dl-pdf": return void downloadPdf();
      case "guide": {
        const g = st.guide, t = st.t;
        if (!g || !t) return;
        // keep this Student Book page's notes (and its tracks) beside the Teacher's Guide page
        if (st.teaching && LG_TEACHING.test(t.bookId)) st.carried = { entry: st.teaching, bookId: t.bookId };
        returns.push({ t, i: st.i, whole: st.whole });
        return openReader({ bookId: g.book_id, book: g.book, label: g.label, pages: g.pdf, teacherOnly: true, ...(t.from ? { from: t.from } : {}) }, st.opener, true);
      }
      case "notes": {
        st.notes = !st.notes;
        b.setAttribute("aria-pressed", String(st.notes));
        q<HTMLElement>('[data-rd-side]').hidden = !st.notes || st.present;
        return applyZoom();
      }
      case "present": {
        if (st.t?.teacherOnly || st.t?.missing) return;
        if (st.present) exitPresent();
        else {
          st.present = true; root!.classList.add("rd-present");
          void root!.requestFullscreen?.().catch(() => {});
        }
        b.textContent = st.present ? "Exit present" : "Present";
        b.setAttribute("aria-pressed", String(st.present));
        q<HTMLElement>('[data-rd-side]').hidden = st.present || !st.notes || !st.teaching;
        return applyZoom();
      }
      case "unit": {
        const t = st.t;
        if (!t?.unit) return;
        stopAudio();
        const current = st.set[st.i]?.pdf;
        st.whole = !st.whole;
        st.set = expandPages(st.whole ? t.unit.pages : t.pages);
        const at = st.set.findIndex((p) => p.pdf === current);
        st.i = at >= 0 ? at : 0;
        st.zoom = 1;
        return render();
      }
    }
  });

  // double-tap / double-click: zoom in on that spot, or back to the whole page
  const view = el.querySelector<HTMLElement>("[data-rd-view]")!;
  view.addEventListener("dblclick", (e) => {
    const r = view.getBoundingClientRect();
    const fx = (e.clientX - r.left + view.scrollLeft) / Math.max(1, img.offsetWidth);
    const fy = (e.clientY - r.top + view.scrollTop) / Math.max(1, img.offsetHeight);
    st.zoom = st.zoom > 1 ? 1 : 2;
    applyZoom();
    if (st.zoom > 1) {
      view.scrollLeft = fx * img.offsetWidth - view.clientWidth / 2;
      view.scrollTop = fy * img.offsetHeight - view.clientHeight / 2;
    }
  });

  // swipe (at the whole-page size) turns the page; two fingers pinch to zoom
  let sx: number | null = null, sy = 0, pinch0 = 0, zoom0 = 1;
  const dist = (t: TouchList) => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
  view.addEventListener("touchstart", (e) => {
    if (e.touches.length === 2) { pinch0 = dist(e.touches); zoom0 = st.zoom; sx = null; }
    else if (e.touches.length === 1 && st.zoom <= 1) { sx = e.touches[0]!.clientX; sy = e.touches[0]!.clientY; }
  }, { passive: true });
  view.addEventListener("touchmove", (e) => {
    if (e.touches.length !== 2 || !pinch0) return;
    e.preventDefault();
    st.zoom = Math.max(1, Math.min(3, zoom0 * (dist(e.touches) / pinch0)));
    applyZoom(true);
  }, { passive: false });
  view.addEventListener("touchend", (e) => {
    if (pinch0 && e.touches.length < 2) { pinch0 = 0; if (st.zoom < 1.1) { st.zoom = 1; applyZoom(); } return; }
    if (sx === null) return;
    const dx = e.changedTouches[0]!.clientX - sx, dy = e.changedTouches[0]!.clientY - sy;
    sx = null;
    if (Math.abs(dx) > 60 && Math.abs(dy) < 60) go(st.i + (dx < 0 ? 1 : -1));
  });

  window.addEventListener("resize", () => { if (!el.hidden) applyZoom(); });
  document.addEventListener("fullscreenchange", () => {
    if (!document.fullscreenElement && st.present) {
      exitPresent();
      q<HTMLElement>('[data-rd="present"]').textContent = "Present";
      q<HTMLElement>('[data-rd="present"]').setAttribute("aria-pressed", "false");
      q<HTMLElement>('[data-rd-side]').hidden = !st.notes || !st.teaching;
      applyZoom();
    }
  });
}

/** Once, at boot (before the router, so this popstate listener runs first). */
export function initBookReader(): void {
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-book-open]");
    if (!b) return;
    const t = targets.get(b.dataset.bookOpen ?? "");
    if (!t) return;
    e.preventDefault();
    openReader(t, b);
  });
  document.addEventListener("keydown", (e) => {
    if (!root || root.hidden) return;
    if (e.key === "Escape") {
      e.preventDefault();
      const menu = root.querySelector<HTMLElement>("[data-rd-dlmenu]");
      if (menu && !menu.hidden) { closeDownloadMenu(); root.querySelector<HTMLElement>('[data-rd="download"]')?.focus(); return; } // focus back on the button that opened it
      if (st.present) { exitPresent(); q<HTMLElement>('[data-rd="present"]').textContent = "Present"; render(); }
      else closeReader();
    }
    else if (e.key === "ArrowRight") go(st.i + 1);
    else if (e.key === "ArrowLeft") go(st.i - 1);
    else if (e.key === "+" || e.key === "=") zoomStep(1);
    else if (e.key === "-") zoomStep(-1);
  });
  // The reader's own history entry: Back closes it, and the router (which re-renders on popstate) never sees it.
  // From a Teacher's Guide page opened on a Student Book page, the phone's Back steps back to that Student Book page
  // first — the same as Escape and the Back button on screen — and only the next Back closes the reader.
  window.addEventListener("popstate", (e) => {
    if (st.closingByButton) { st.closingByButton = false; e.stopImmediatePropagation(); return; }
    if (!root || root.hidden) return;
    e.stopImmediatePropagation();
    st.pushed = false; // the browser has just used up the reader's entry
    if (returns.length) closeReader(); // back to the page it came from; openReader() takes a fresh entry
    else closeReader(true);
  });
}
